<template>
	<div>
		<Dialog
			v-model="dialogVisible"
			width="90%"
			:title="title"
			:confirmButtonEnabled="!isDistanceValidating"
			noCancelButton
			@confirm="handleConfirm"
			@closeDialog="handleCloseDialog"
		>
			<div class="methods-wrapper">
				<div
					class="method-item"
					v-for="method in availableMethods"
					:key="method"
					:class="{
						active: selectedMethod === method,
						freezed: selectedMethod === method,
					}"
					@click="handleSelectMethod(method)"
				>
					{{ displayMethodsText[method] }}
				</div>
			</div>
			<DineInForm
				:errorValidate="errorValidate"
				v-if="selectedMethod === 'DINE_IN'"
				:pickUpDraft="pickUpDraft"
				@update:input="handleUpdateInput"
			/>
			<SelfForm
				:errorValidate="errorValidate"
				v-if="selectedMethod === 'SELF'"
				:class="{
					'pickup-time-expanded': pickUpDraft.pickUpTimeType === 'TIME',
					'pickup-time-collapsed': pickUpDraft.pickUpTimeType === 'NOW',
				}"
				:pickUpDraft="pickUpDraft"
				@update:input="handleUpdateInput"
			/>
			<DeliveryForm
				:errorValidate="errorValidate"
				v-if="selectedMethod === 'DELIVERY'"
				:maxDistance="maxDistance"
				:minAmount="minAmount"
				:pickUpDraft="pickUpDraft"
				@update:input="handleUpdateInput"
			/>
		</Dialog>
	</div>
</template>

<script>
import Dialog from './Dialog.vue'
import DineInForm from './PickupMethodForm/DineIn.vue'
import SelfForm from './PickupMethodForm/Self.vue'
import DeliveryForm from './PickupMethodForm/Delivery.vue'
import { isValidTaiwanPhone } from '@/utils/validate'

export default {
	components: { Dialog, DineInForm, SelfForm, DeliveryForm },
	name: 'PickupMethodDialog',
	props: {
		width: {
			type: String,
			default: '465px',
		},
		title: {
			type: String,
			default: '選擇取餐方式和時間',
		},
		isShowing: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			displayMethodsText: {
				DINE_IN: '內用',
				SELF: '外帶自取',
				DELIVERY: '外送',
			},
			selectedMethod: null,
			pickUpDraft: {
				tableNumber: null,
				userName: null,
				phone: null,
				address: null,
				pickUpTimeType: null,
				pickUpDate: null,
				pickUpTime: null,
			},
			isDistanceValidating: false,
			errorValidate: {
				tableNumber: false,
				userName: false,
				phone: false,
				phoneFormat: false,
				address: false,
				pickUpTimeType: false,
				pickUpDate: false,
				pickUpTime: false,
			},
			errorMessage: {
				userName: '姓名不能為空',
				phone: '電話不能為空',
				tableNumber: '桌號不能為空',
				phoneFormat: '電話格式不正確',
				address: '地址不能為空',
				pickUpTimeType: '取餐時間不能為空',
				pickUpDate: '取餐日期不能為空',
				pickUpTime: '取餐時間不能為空',
				selectedMethod: '取餐方式不能為空',
			},
		}
	},
	model: {
		prop: 'isShowing',
		event: 'update:isShowing',
	},
	computed: {
		dialogVisible: {
			get() {
				return this.isShowing
			},
			set(value) {
				this.$emit('update:isShowing', value)
			},
		},
		maxDistance() {
			const maxDistance =
				this.$store.state.store?.openInfo?.mobilePickupMethod?.delivery
					?.deliveryLongestDistance
			return maxDistance ? maxDistance : null
		},
		minAmount() {
			const minAmount =
				this.$store.state.store?.openInfo?.mobilePickupMethod?.delivery
					?.deliveryMinimumAmount
			return minAmount ? minAmount : null
		},
		availableMethods() {
			if (!this.$store.state.store?.openInfo?.mobilePickupMethod) {
				return []
			}
			let rawMethods = Object.entries(
				this.$store.state.store.openInfo.mobilePickupMethod,
			)
				.filter(([key, value]) => value.methodSwitch)
				.map(([key]) => key)
			const methodsMap = {
				dineIn: 'DINE_IN',
				self: 'SELF',
				delivery: 'DELIVERY',
			}
			if (!this.isStoreOpen) {
				rawMethods = rawMethods.filter(method => method !== 'dineIn')
			}
			return rawMethods.map(method => methodsMap[method])
		},
		isAvailableDineInSwitch() {
			return this.$store.state.store?.openInfo?.mobilePickupMethod?.dineIn
		},
		isAvailableDeliverySwitch() {
			return this.$store.state.store?.openInfo?.mobilePickupMethod?.delivery
		},
		isAvailableSelfSwitch() {
			return this.$store.state.store?.openInfo?.mobilePickupMethod?.self
		},
		isStoreOpen() {
			return this.$store.getters.isOpen
		},
	},
	methods: {
		handleUpdateInput(input) {
			this.pickUpDraft = { ...this.pickUpDraft, ...input }
		},
		attempToCloseDialog() {
			this.$emit('update:isShowing', false)
		},
		handleCancel() {
			this.attempToCloseDialog()
			this.$emit('cancel')
		},
		async handleConfirm() {
			const { result, errorField } = this.checkFormIsValid()
			if (!result) {
				this.$alert(this.errorMessage[errorField], 'danger')
				return
			}

			if (this.selectedMethod === 'DELIVERY') {
				const { isValid, message } = await this.validateDeliveryDistance()
				if (!isValid) {
					this.$alert(message, 'danger')
					return
				}
			}

			this.$emit('confirm', {
				...this.pickUpDraft,
				pickUpMethod: this.selectedMethod,
			})
			this.clearPickUpDraft()
			this.attempToCloseDialog()
		},
		async validateDeliveryDistance() {
			try {
				this.isDistanceValidating = true
				const address = this.pickUpDraft.address
				const storeId = this.$store.state?.store?.openInfo?.storeId
				const deliveryDistance = await this.$api.checkDeliveryDistance({
					storeId,
					address,
				})
				if (deliveryDistance.errorCode === 'SUCCESS') {
					this.$store.commit(
						'user/SET_DELIVERY_CHECK_STATUS',
						deliveryDistance.data,
					)
					return { isValid: true }
				}
			} catch (error) {
				return { isValid: false, message: error }
			} finally {
				this.isDistanceValidating = false
			}
		},
		checkFormIsValid() {
			if (!this.selectedMethod) {
				this.showErrorHint('selectedMethod')
				return { result: false, errorField: 'selectedMethod' }
			}
			switch (this.selectedMethod) {
				case 'DINE_IN':
					if (
						!this.pickUpDraft.userName &&
						this.isAvailableDineInSwitch?.nameSwitch
					) {
						this.showErrorHint('userName')
						return { result: false, errorField: 'userName' }
					}
					if (
						!this.pickUpDraft.tableNumber &&
						this.isAvailableDineInSwitch?.tableNumberSwitch
					) {
						this.showErrorHint('tableNumber')
						return { result: false, errorField: 'tableNumber' }
					}
					if (
						!this.pickUpDraft.phone &&
						this.isAvailableDineInSwitch?.phoneSwitch
					) {
						this.showErrorHint('phone')
						return { result: false, errorField: 'phone' }
					}
					if (
						!isValidTaiwanPhone(this.pickUpDraft.phone) &&
						this.isAvailableDineInSwitch?.phoneSwitch
					) {
						this.showErrorHint('phoneFormat')
						return { result: false, errorField: 'phoneFormat' }
					}
					return { result: true }
				case 'SELF':
					if (
						!this.pickUpDraft.userName &&
						this.isAvailableSelfSwitch?.nameSwitch
					) {
						this.showErrorHint('userName')
						return { result: false, errorField: 'userName' }
					}
					if (
						!this.pickUpDraft.phone &&
						this.isAvailableSelfSwitch?.phoneSwitch
					) {
						this.showErrorHint('phone')
						return { result: false, errorField: 'phone' }
					}
					if (
						!isValidTaiwanPhone(this.pickUpDraft.phone) &&
						this.isAvailableSelfSwitch?.phoneSwitch
					) {
						this.showErrorHint('phoneFormat')
						return { result: false, errorField: 'phoneFormat' }
					}
					if (!this.pickUpDraft.pickUpTimeType) {
						this.showErrorHint('pickUpTimeType')
						return { result: false, errorField: 'pickUpTimeType' }
					}
					if (this.pickUpDraft.pickUpTimeType === 'NOW') {
						return { result: true }
					} else if (this.pickUpDraft.pickUpTimeType === 'TIME') {
						if (!this.pickUpDraft.pickUpDate) {
							this.showErrorHint('pickUpDate')
							const takeDateElements = document.querySelectorAll('.by-picker')
							takeDateElements.forEach(element => {
								element.style.border = 'none' // 隱藏黑框
							})
							return { result: false, errorField: 'pickUpDate' }
						} else {
							if (!this.pickUpDraft.pickUpTime) {
								this.showErrorHint('pickUpTime')
								return { result: false, errorField: 'pickUpTime' }
							}
							return { result: true }
						}
					} else {
						return { result: false, errorField: 'pickUpTimeType' }
					}
				case 'DELIVERY':
					if (
						!this.pickUpDraft.userName &&
						this.isAvailableDeliverySwitch?.nameSwitch
					) {
						this.showErrorHint('userName')
						return { result: false, errorField: 'userName' }
					}
					if (
						!this.pickUpDraft.phone &&
						this.isAvailableDeliverySwitch?.phoneSwitch
					) {
						this.showErrorHint('phone')
						return { result: false, errorField: 'phone' }
					}
					if (
						!isValidTaiwanPhone(this.pickUpDraft.phone) &&
						this.isAvailableDeliverySwitch?.phoneSwitch
					) {
						this.showErrorHint('phoneFormat')
						return { result: false, errorField: 'phoneFormat' }
					}
					if (!this.pickUpDraft.address) {
						this.showErrorHint('address')
						return { result: false, errorField: 'address' }
					}
					if (!this.pickUpDraft.pickUpDate) {
						this.showErrorHint('pickUpDate')
						return { result: false, errorField: 'pickUpDate' }
					}
					if (!this.pickUpDraft.pickUpTime) {
						this.showErrorHint('pickUpTime')
						return { result: false, errorField: 'pickUpTime' }
					}
					return { result: true }
				default:
					return false
			}
		},
		showErrorHint(field) {
			this.errorValidate[field] = true
			setTimeout(() => {
				this.errorValidate[field] = false
				const takeDateElements = document.querySelectorAll('.by-picker')
				takeDateElements.forEach(element => {
					element.style.border = '0px solid' // 恢復圓框
				})
			}, 3000)
		},
		clearPickUpDraft() {
			this.pickUpDraft = {
				...this.pickUpDraft,
				tableNumber: null,
				pickUpTimeType: null,
				pickUpDate: null,
				pickUpTime: null,
			}
		},
		handleCloseDialog() {
			this.attempToCloseDialog()
		},
		handleSelectMethod(method) {
			this.selectedMethod = method
			// NOTE: 切換取餐類型時，清理不相關的欄位，避免資料污染。再由表單 mounted 時把資料塞回來
			this.clearPickUpDraft()
		},
		loadPickUpDataFromStore() {
			const {
				pickUpMethod,
				tableNumber,
				userName,
				userPhone: phone,
				pickUpTimeType,
				pickUpDate,
				pickUpTime,
			} = this.$store.state.user
			const address = this.$store.state.user.guestUserPreferences.address
			this.selectedMethod = pickUpMethod

			// 直接載入所有值，送出時接收端會清洗
			this.pickUpDraft = {
				userName: userName || null,
				phone: phone || null,
				tableNumber: tableNumber || null,
				address: address || null,
				pickUpTimeType: pickUpTimeType || null,
				pickUpDate: pickUpDate || null,
				pickUpTime: pickUpTime || null,
			}
		},
	},
	watch: {
		'$store.state.store.openInfo': {
			handler(val) {
				if (!val) return
				const list = this.availableMethods
				if (!list.length) return

				// NOTE: 在初次選擇時，如果有複選選項，就不預選。如果只有一個選項，則自動選取
				if (
					this.selectedMethod === null &&
					!this.$store.state.user.pickUpMethod
				) {
					if (list.length === 1) {
						this.selectedMethod = list[0]
					}
					return
				}

				// NOTE: 有目前暫選的狀態，如果目前 selectedMethod 不在可用清單中，則切換到第一筆
				if (!list.includes(this.selectedMethod)) {
					this.selectedMethod = list[0]
				}
			},
		},
	},
	mounted() {
		/**
		 * NOTE:
		 * 這邊並非在跳窗出現時才會執行，而是只在初始化。
		 * 如果想要在跳窗出現時才會執行，要改在 watch 中監聽 isShowing 屬性來執行。
		 * 但是如果改成監聽 isShowing 屬性來執行的話，會有一些資料選取上的邏輯需要調整
		 * 雖然整體有些冗餘，這邊目前的好處是，使用者不小心重整時，還會有資料可以讀取
		 * */
		this.clearPickUpDraft()
		this.selectedMethod = null
		this.$store.state.user.pickUpMethod && this.loadPickUpDataFromStore()
	},
}
</script>

<style lang="sass" scoped>
.methods-wrapper
	display: flex
	gap: 6px
	margin-bottom: 10px

.method-item
	display: flex
	align-items: center
	justify-content: center
	border: 2px solid #D7D7D7
	border-radius: 6px
	padding: 10px
	flex: 1

.method-item.active
	background-color: #294B70
	color: #fff
	border-color: #294B70

.method-item.freezed
	pointer-events: none

.pickUp-method-form
	transition: height 0.3s ease

	&.pickup-time-collapsed
		height: 200px

	&.pickup-time-expanded
		min-height: 280px

::v-deep .form-row
	display: flex
	align-items: flex-end
	gap: 10px

::v-deep .form-row:not(:last-child)
	margin-bottom: 10px

/** NOTE: 這邊這個處理是因為 Input 元件的最外層是由 form 來去控制的 */
.pickUp-method-form ::v-deep .form-row form
	flex: 1

.pickUp-method-form ::v-deep input[type="text"]
	padding-left: 4rem !important

::v-deep .error-hint
	font-size: 12.5px
	color: red
</style>
